import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { Editor } from '@tiptap/react';
import React from 'react';

function ToolbarBtn({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: 1,
            p: 0.5,
            color: active ? 'primary.main' : 'text.secondary',
            bgcolor: active ? 'primary.50' : 'transparent',
            '&:hover': { bgcolor: active ? 'primary.100' : 'action.hover' },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function VeraPromptEditorToolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;
  const iconSx = { fontSize: 18 };
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.25,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        borderRadius: '4px 4px 0 0',
      }}
    >
      <ToolbarBtn
        label="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FormatBoldIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FormatItalicIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikethroughSIcon sx={iconSx} />
      </ToolbarBtn>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {([1, 2, 3] as const).map((level) => (
        <ToolbarBtn
          key={level}
          label={`Heading ${level}`}
          active={editor.isActive('heading', { level })}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1, width: 18, textAlign: 'center' }}
          >
            H{level}
          </Typography>
        </ToolbarBtn>
      ))}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ToolbarBtn
        label="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FormatListBulletedIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Ordered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FormatListNumberedIcon sx={iconSx} />
      </ToolbarBtn>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ToolbarBtn
        label="Blockquote"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <FormatQuoteIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Inline code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <CodeIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Code block"
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <DataObjectIcon sx={iconSx} />
      </ToolbarBtn>
      <ToolbarBtn
        label="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <HorizontalRuleIcon sx={iconSx} />
      </ToolbarBtn>
    </Box>
  );
}

export function tiptapToMarkdown(json: any): string {
  const serializeNode = (node: any): string => {
    if (!node) return '';
    switch (node.type) {
      case 'doc':
        return (node.content ?? []).map(serializeNode).join('\n\n');
      case 'paragraph':
        return serializeInline(node.content ?? []);
      case 'heading': {
        const level = node.attrs?.level ?? 1;
        return '#'.repeat(level) + ' ' + serializeInline(node.content ?? []);
      }
      case 'bulletList':
        return (node.content ?? []).map((li: any) => '- ' + serializeListItem(li)).join('\n');
      case 'orderedList':
        return (node.content ?? [])
          .map((li: any, i: number) => `${i + 1}. ` + serializeListItem(li))
          .join('\n');
      case 'codeBlock': {
        const lang = node.attrs?.language ?? '';
        const code = (node.content ?? []).map((n: any) => n.text ?? '').join('');
        return '```' + lang + '\n' + code + '\n```';
      }
      case 'blockquote':
        return (node.content ?? [])
          .map(serializeNode)
          .join('\n')
          .split('\n')
          .map((l: string) => '> ' + l)
          .join('\n');
      case 'horizontalRule':
        return '---';
      default:
        return (node.content ?? []).map(serializeNode).join('');
    }
  };

  const serializeInline = (nodes: any[]): string =>
    (nodes ?? [])
      .map((n) => {
        if (n.type === 'text') {
          let t = n.text ?? '';
          const marks: string[] = (n.marks ?? []).map((m: any) => m.type);
          if (marks.includes('code')) t = '`' + t + '`';
          if (marks.includes('bold')) t = '**' + t + '**';
          if (marks.includes('italic')) t = '_' + t + '_';
          if (marks.includes('strike')) t = '~~' + t + '~~';
          return t;
        }
        return serializeNode(n);
      })
      .join('');

  const serializeListItem = (li: any): string => {
    const paras = (li.content ?? []).map(serializeNode);
    return paras.join(' ').replace(/\n+/g, ' ').trim();
  };

  return serializeNode(json).trim();
}

export function markdownToTiptapJson(md: string): object {
  const lines = md.split('\n');
  const content: any[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        code.push(lines[i]);
        i++;
      }
      content.push({
        type: 'codeBlock',
        attrs: { language: lang || null },
        content: [{ type: 'text', text: code.join('\n') }],
      });
      i++;
      continue;
    }
    const hMatch = line.match(/^(#{1,6})\s+(.*)/);
    if (hMatch) {
      content.push({
        type: 'heading',
        attrs: { level: hMatch[1].length },
        content: [{ type: 'text', text: hMatch[2] }],
      });
      i++;
      continue;
    }
    if (line.match(/^[-*]\s/)) {
      content.push({
        type: 'bulletList',
        content: [
          {
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: line.slice(2) }] }],
          },
        ],
      });
      i++;
      continue;
    }
    if (line.trim() === '') {
      i++;
      continue;
    }
    content.push({ type: 'paragraph', content: [{ type: 'text', text: line }] });
    i++;
  }
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}
